import { useEffect, useRef } from 'react'
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline'
import { Button } from '@cottage/design-system/internal-tooling/ui'
import SearchableSelect, { type SelectOption } from '@/components/SearchableSelect'
import { adjacentReportMonthId } from '@/components/adjacentReportMonth'

export type ReportMonthSelectProps = {
  id: string
  options: SelectOption[]
  value: string | null
  onChange: (id: string | null) => void
  disabled?: boolean
  placeholder?: string
  className?: string
  wrapperClassName?: string
}

function isTextEntry(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false
  if (target.isContentEditable) return true
  if (target.tagName === 'TEXTAREA' || target.tagName === 'SELECT') return true
  if (!(target instanceof HTMLInputElement)) return false
  return !['button', 'checkbox', 'radio', 'submit', 'reset', 'file', 'hidden'].includes(target.type)
}

export default function ReportMonthSelect({
  id,
  options,
  value,
  onChange,
  disabled = false,
  placeholder,
  className,
  wrapperClassName,
}: ReportMonthSelectProps) {
  const rootRef = useRef<HTMLDivElement>(null)
  const armedRef = useRef(false)
  const navRef = useRef({ prevId: null as string | null, nextId: null as string | null, onChange, disabled })

  const controlsDisabled = disabled || !options.length
  const valueInOptions = Boolean(value && options.some((option) => option.id === value))
  const prevId = valueInOptions ? adjacentReportMonthId(options, value, 'prev') : null
  const nextId = valueInOptions ? adjacentReportMonthId(options, value, 'next') : null
  navRef.current = { prevId, nextId, onChange, disabled: controlsDisabled }

  function stepMonth(direction: 'prev' | 'next'): boolean {
    const nav = navRef.current
    if (nav.disabled) return false
    const monthId = direction === 'prev' ? nav.prevId : nav.nextId
    if (!monthId) return false
    nav.onChange(monthId)
    return true
  }

  function handleWidgetKeyDown(event: { key: string; altKey: boolean; ctrlKey: boolean; metaKey: boolean; preventDefault: () => void; stopPropagation: () => void }) {
    if (event.altKey || event.ctrlKey || event.metaKey) return
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return
    event.preventDefault()
    event.stopPropagation()
    stepMonth(event.key === 'ArrowLeft' ? 'prev' : 'next')
  }

  useEffect(() => {
    const root = rootRef.current
    if (!root) return

    const arm = () => {
      armedRef.current = true
    }

    const onFocusIn = (event: FocusEvent) => {
      if (root.contains(event.target as Node)) {
        arm()
        return
      }
      const menuOpen = Boolean(root.querySelector('[aria-expanded="true"]'))
      const inOurSearch = menuOpen && event.target instanceof HTMLInputElement && !root.contains(event.target)
      if (inOurSearch) {
        arm()
        return
      }
      if (isTextEntry(event.target)) {
        armedRef.current = false
      }
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.altKey || event.ctrlKey || event.metaKey) return
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return

      const target = event.target
      const active = document.activeElement
      const inRoot =
        (target instanceof Node && root.contains(target)) ||
        (active instanceof Node && root.contains(active))
      const menuOpen = Boolean(root.querySelector('[aria-expanded="true"]'))
      const inOurSearch =
        menuOpen &&
        ((target instanceof HTMLElement && !root.contains(target)) ||
          (active instanceof HTMLElement && !root.contains(active)))

      if (!inRoot && !inOurSearch && !armedRef.current) return
      if (isTextEntry(target) && !inRoot && !inOurSearch) return

      event.preventDefault()
      event.stopPropagation()
      stepMonth(event.key === 'ArrowLeft' ? 'prev' : 'next')
    }

    root.addEventListener('pointerenter', arm)
    root.addEventListener('pointerdown', arm)
    document.addEventListener('focusin', onFocusIn)
    window.addEventListener('keydown', onKeyDown, true)
    return () => {
      root.removeEventListener('pointerenter', arm)
      root.removeEventListener('pointerdown', arm)
      document.removeEventListener('focusin', onFocusIn)
      window.removeEventListener('keydown', onKeyDown, true)
    }
  }, [])

  return (
    <div
      ref={rootRef}
      role="group"
      aria-label="Month"
      className={['flex h-10 items-stretch gap-2', wrapperClassName].filter(Boolean).join(' ')}
    >
      <Button
        type="button"
        variant="outline"
        iconOnly
        size="lg"
        className="shrink-0"
        aria-label="Previous month"
        disabled={controlsDisabled || !prevId}
        onClick={() => {
          if (prevId) {
            onChange(prevId)
          }
        }}
        onKeyDown={handleWidgetKeyDown}
      >
        <ChevronLeftIcon className="h-5 w-5" />
      </Button>
      <SearchableSelect
        id={id}
        options={options}
        value={value}
        onChange={onChange}
        disabled={disabled}
        preserveOrder
        preferBelow
        useViewportForPlacement
        fillHeight
        placeholder={placeholder}
        className={['h-full', className].filter(Boolean).join(' ')}
        onKeyDown={handleWidgetKeyDown}
      />
      <Button
        type="button"
        variant="outline"
        iconOnly
        size="lg"
        className="shrink-0"
        aria-label="Next month"
        disabled={controlsDisabled || !nextId}
        onClick={() => {
          if (nextId) {
            onChange(nextId)
          }
        }}
        onKeyDown={handleWidgetKeyDown}
      >
        <ChevronRightIcon className="h-5 w-5" />
      </Button>
    </div>
  )
}
